import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useEffect } from 'react';
import { EvaluationTypeIcon } from '@/app/components/features/evaluations/EvaluationTypeIcon';
import { getTenderPackageDetailsQuery } from '@/app/hooks/queries/useTenderPackages';
import {
  Breadcrumb,
  BreadcrumbMenu,
  BreadcrumbMenuItem,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/app/ui/components/breadcrumb';
import {
  Header,
  HeaderSidechatButton,
  HeaderBackButton,
  HeaderSidebarTrigger,
} from '@/app/ui/components/header';
import {
  EvaluationTypeLabelsEnum,
  ProxyDocumentTypes,
  queryKeys,
  REFETCH_SUMMARIZATION_INTERVAL,
  SummarizationNamespaces,
} from '@/app/utils/constants';
import { DocumentAlerts } from '@/app/components/shared/EntityDocuments/DocumentAlerts';
import { FloatingAlert } from '@/app/components/shared/FloatingAlert';
import {
  AuctionIcon,
  Alert01Icon,
  Tick02Icon,
  FileAttachmentIcon,
  InformationCircleIcon,
  SecurityIcon,
} from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { useTranslations } from 'use-intl';
import { MainContainer, MainHeaderBackButton, MainScrollArea } from '@/app/ui/components/main';
import { H1, H2 } from '@/app/ui/components/heading';
import { Spinner } from '@/app/ui/components/spinner';
import {
  useBidMethodologySummaryQuery,
  useBidQualityCategoriesQuery,
  useBidQualityAnalysisMutation,
} from '@/app/hooks/queries/useBidQuality';
import useDocumentsIndexingStatus from '@/app/hooks/useDocumentsIndexingStatus';
import { Card } from '@/app/ui/components/card';
import { getBidDetailsQuery } from '@/app/hooks/queries/useBids';
import { Badge } from '@/app/ui/components/badge';
import { Alert, AlertDescription } from '@/app/ui/components/alert';
import { CircularProgress } from '@/app/ui/components/circular-progress';
import { getProjectDetailsQuery } from '@/app/hooks/queries/useProjects';
import { useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { ProjectTypeIcon } from '@/app/components/features/projects/ProjectTypeIcon';
import { getAssessmentLabelKey, getBidAssessmentColor } from '@/app/components/features/bids/utils';

export const Route = createFileRoute(
  '/_authed/project/$projectId/_layout/(evaluation)/tender-packages/$tenderPackageId/bids/$bidId/_layout/methodology/',
)({
  component: RouteComponent,
});

function RouteComponent() {
  const t = useTranslations('pages.bids');
  const tBidQuality = useTranslations('components.bidQuality');
  const tGeneral = useTranslations('general');
  const queryClient = useQueryClient();
  const { bidId, tenderPackageId, projectId } = Route.useParams();
  const { data: bid } = useSuspenseQuery(getBidDetailsQuery(bidId));
  const { data: evaluation } = useSuspenseQuery(getTenderPackageDetailsQuery(tenderPackageId));
  const { data: project } = useSuspenseQuery(getProjectDetailsQuery(projectId));
  const navigate = useNavigate();
  const { data, isLoading } = useBidQualityCategoriesQuery({
    bidId,
    enabled: true,
    refetchInterval: REFETCH_SUMMARIZATION_INTERVAL * 1000,
  });
  const { data: summary } = useBidMethodologySummaryQuery({ bidId, enabled: true });

  const { mutateAsync: triggerAnalysis, isPending: isMutationPending } =
    useBidQualityAnalysisMutation(bidId);

  const isProcessing = data?.status === 10;
  const versionStatus = data?.versionStatus;

  const { areDocumentsIndexed } = useDocumentsIndexingStatus({
    documentType: ProxyDocumentTypes.BID,
    id: bidId,
  });

  useEffect(() => {
    if (versionStatus === 'stale') {
      void queryClient.invalidateQueries({
        queryKey: [queryKeys.INDEXING_STATUS, ProxyDocumentTypes.BID, bidId],
      });
    }
  }, [versionStatus, queryClient, bidId]);

  const totalScore = summary?.totalScore ?? 0;
  const good = summary?.good ?? data?.good ?? 0;
  const needsImprovement = summary?.needsImprovement ?? data?.needsImprovement ?? 0;
  const total = good + needsImprovement;

  return (
    <>
      <Header>
        <HeaderBackButton />
        <HeaderSidebarTrigger />
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbMenu>
              <BreadcrumbMenuItem
                to="/project/$projectId/tender-packages/$tenderPackageId"
                params={{ projectId, tenderPackageId }}
              >
                <EvaluationTypeIcon type={EvaluationTypeLabelsEnum.TENDER_PACKAGE} />
                {evaluation.name}
              </BreadcrumbMenuItem>
              <BreadcrumbMenuItem to="/project/$projectId" params={{ projectId }}>
                <ProjectTypeIcon type={project.type} />
                {project.name}
              </BreadcrumbMenuItem>
            </BreadcrumbMenu>
            <BreadcrumbSeparator className="max-md:hidden" />
            <BreadcrumbItem className="max-md:hidden">
              <BreadcrumbLink
                to="/project/$projectId/tender-packages/$tenderPackageId/bids"
                params={{ projectId, tenderPackageId }}
              >
                <HugeiconsIcon icon={AuctionIcon} />
                {t('title')}
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator className="max-md:hidden" />
            <BreadcrumbItem>
              <BreadcrumbLink
                to="/project/$projectId/tender-packages/$tenderPackageId/bids/$bidId"
                params={{ projectId, tenderPackageId, bidId }}
              >
                <span className="line-clamp-1">{bid.bidAdditionalName || bid.companyName}</span>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>{tBidQuality('cards.methodology.title')}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
        <HeaderSidechatButton />
      </Header>
      <MainScrollArea>
        <DocumentAlerts
          resourceId={bidId}
          summaryType={SummarizationNamespaces.BID}
          onGoToFiles={() =>
            navigate({
              to: '/project/$projectId/tender-packages/$tenderPackageId/bids/$bidId/files',
              params: { projectId, tenderPackageId, bidId },
            })
          }
        />
        {isLoading ? (
          <Spinner kind="inset" />
        ) : (
          <MainContainer>
            <H1 className="mb-6">
              <MainHeaderBackButton />
              {tBidQuality('cards.methodology.title')}
            </H1>
            <Alert variant="info" className="mb-5">
              <HugeiconsIcon icon={InformationCircleIcon} />
              <AlertDescription>{tBidQuality('exclusionWarning.description')}</AlertDescription>
            </Alert>
            {summary?.overallSummary && (
              <section>
                <H2 className="mb-4 mt-6">{tBidQuality('labels.executiveSummary')}</H2>
                <div className="space-y-2">
                  <p className="text-sm font-medium">{tBidQuality('labels.overallAssessment')}</p>
                  <p className="text-sm text-muted">{summary.overallSummary}</p>
                </div>
              </section>
            )}
            <section>
              <H2 className="mb-4 mt-6"> {tBidQuality('labels.bidMethodologyBreakdown')}</H2>
              <div className="flex items-center gap-6 md:gap-8">
                <CircularProgress
                  max={10}
                  getValueText={(value) => value.toFixed(1)}
                  value={totalScore}
                  size={110}
                  label={tBidQuality('labels.score')}
                  color={getBidAssessmentColor(totalScore)}
                />
                <div className="flex-1 space-y-4">
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted">{tBidQuality('labels.good')}</span>
                      <span className="text-sm font-medium">{good}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted">
                        {tBidQuality('labels.needsImprovement')}
                      </span>
                      <span className="text-sm font-medium">{needsImprovement}</span>
                    </div>
                    <div className="flex items-center justify-between border-t pt-2">
                      <span className="text-sm font-medium">{tBidQuality('labels.total')}</span>
                      <span className="text-sm font-medium">{total}</span>
                    </div>
                  </div>
                </div>
              </div>
            </section>
            <section>
              <H2 className="mb-4 mt-6">{tBidQuality('labels.criteriaAnalysis')}</H2>
              <div className="space-y-4">
                {data?.categories.map((category) => {
                  const sections = category.sections ?? [];
                  const avgScore =
                    sections.length > 0
                      ? sections.reduce((acc, s) => acc + (s.totalScore ?? 0), 0) / sections.length
                      : 0;
                  const sectionCount = sections.length;

                  return (
                    <Card key={category.title} className="p-0 overflow-hidden">
                      <div className="flex items-start p-4 justify-between">
                        <div className="flex items-start gap-4">
                          <HugeiconsIcon icon={SecurityIcon} className="size-6 text-muted" />
                          <div className="space-y-1">
                            <p className="text-sm font-medium">{category.title}</p>
                            <p className="text-xs text-muted">
                              {tBidQuality('fileDetails.criteria')}
                            </p>
                            <div className="flex items-center gap-2">
                              <p className="text-xs text-muted">
                                {tBidQuality('toolbar.sections')}: {sectionCount}{' '}
                                {tBidQuality('toolbar.avgScore')}: {avgScore.toFixed(1)}
                              </p>
                              {sectionCount > 0 && <div className="h-1 w-24 rounded bg-success" />}
                            </div>
                          </div>
                        </div>
                        <Badge variant="secondary">
                          {category.weight
                            ? `${tBidQuality('labels.weight')}: ${category.weight.toFixed(1)}%`
                            : tBidQuality('fileDetails.notGiven')}
                        </Badge>
                      </div>
                      <div className="divide-y">
                        {sections.map((section) => (
                          <Link
                            key={section.id}
                            className="cursor-pointer block py-3 px-4 hover:bg-surface-2"
                            to="/project/$projectId/tender-packages/$tenderPackageId/bids/$bidId/methodology/$categoryId"
                            params={{ projectId, tenderPackageId, bidId, categoryId: section.id }}
                          >
                            <div className="flex items-start gap-2">
                              <HugeiconsIcon
                                icon={FileAttachmentIcon}
                                className="size-5 mt-0.5 shrink-0"
                              />
                              <p className="text-base font-medium">{section.title}</p>
                            </div>
                            <div className="flex mt-4 items-center gap-5 flex-wrap">
                              <div className="space-y-1">
                                <p className="text-sm font-medium flex items-center gap-1">
                                  <HugeiconsIcon
                                    icon={Tick02Icon}
                                    className="size-5 text-success"
                                  />
                                  {section.good}
                                </p>
                                <p className="text-sm text-muted">{tBidQuality('labels.good')}</p>
                              </div>
                              <div className="space-y-1">
                                <p className="text-sm font-medium flex items-center gap-1">
                                  <HugeiconsIcon
                                    icon={Alert01Icon}
                                    className="size-5 text-warning"
                                  />
                                  {section.needsImprovement}
                                </p>
                                <p className="text-sm text-muted">
                                  {tBidQuality('labels.needsImprovement')}
                                </p>
                              </div>
                              <div className="space-y-1">
                                <p className="text-sm font-medium flex items-center gap-1">
                                  {section.totalScore?.toFixed(1) ?? 0}
                                </p>
                                <p className="text-sm text-muted">
                                  {tBidQuality('labels.sectionScore')}:{' '}
                                  {tBidQuality(
                                    `assessmentLabels.${getAssessmentLabelKey(section.totalScore)}`,
                                  )}
                                </p>
                              </div>
                            </div>
                          </Link>
                        ))}
                      </div>
                    </Card>
                  );
                })}
              </div>
            </section>
          </MainContainer>
        )}
      </MainScrollArea>
      {versionStatus === 'stale' && !isProcessing && (
        <FloatingAlert
          type="warning"
          title={tGeneral('staleAnalysisAlert.title')}
          description={tGeneral('staleAnalysisAlert.description')}
          actionLabel={tGeneral('staleAnalysisAlert.action')}
          onActionClick={async () => {
            await triggerAnalysis();
          }}
          actionPending={isMutationPending}
          actionDisabled={!areDocumentsIndexed}
        />
      )}
    </>
  );
}
